import { forwardRef } from 'react';
import { ShoppingBag, Ruler, Search, Settings, ClipboardList, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCartStore } from '@/store/cartStore';
import { CountryIndicator } from '@/components/CountrySelector';
import { useAuth } from '@/hooks/useAuth';
import { ThemeToggle } from '@/components/ThemeToggle';
import logo from '@/assets/logo.png';

const Header = forwardRef<HTMLElement>((_, ref) => {
  const totalItems = useCartStore((state) => state.getTotalItems());
  const { isAdmin } = useAuth();

  return (
    <header ref={ref} className="sticky top-0 z-40 border-b border-border/70 bg-background/88 backdrop-blur-xl safe-area-top">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:h-[72px] lg:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="أصيل - الرئيسية">
          <img src={logo} alt="أصيل" className="h-10 w-10 rounded-2xl object-cover shadow-sm" loading="eager" />
          <div>
            <span className="block text-lg font-black leading-none text-foreground">أصيل</span>
          </div>
        </Link>

        <div className="flex items-center gap-0.5">
          {isAdmin && (
            <>
              <Link to="/admin/orders" aria-label="طلبات الأدمن" className="hidden rounded-xl p-2.5 transition-colors hover:bg-muted active:scale-95 sm:block">
                <ClipboardList className="w-5 h-5 text-primary" />
              </Link>
              <Link to="/admin/settings" aria-label="إعدادات الأدمن" className="hidden rounded-xl p-2.5 transition-colors hover:bg-muted active:scale-95 sm:block">
                <Settings className="w-5 h-5 text-primary" />
              </Link>
            </>
          )}
          <ThemeToggle compact />
          <CountryIndicator />
          <Link to="/size-guide" aria-label="دليل المقاسات" className="hidden rounded-xl p-2.5 transition-colors hover:bg-muted active:scale-95 sm:block">
            <Ruler className="w-5 h-5 text-foreground" />
          </Link>
          <Link to="/search" aria-label="البحث" className="rounded-xl p-2.5 transition-colors hover:bg-muted active:scale-95">
            <Search className="w-5 h-5 text-foreground" />
          </Link>
          <Link to="/cart" aria-label="سلة التسوق" className="relative rounded-xl bg-primary p-2.5 text-primary-foreground shadow-sm transition hover:-translate-y-0.5">
            <ShoppingBag className="h-5 w-5 text-primary-foreground" />
            {totalItems > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-background bg-accent px-1 text-[10px] font-black text-accent-foreground">
                {totalItems}
              </span>
            )}
          </Link>
          <Link to="/categories" className="mr-1 hidden items-center gap-1.5 rounded-full bg-accent px-4 py-2.5 text-xs font-black text-accent-foreground transition hover:-translate-y-0.5 lg:flex">
            تسوق الآن <ArrowLeft className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </header>
  );
});

Header.displayName = 'Header';

export default Header;
